import React, { useState } from 'react';
import Landing from './components/Landing';
import UploadProgress from './components/UploadProgress';
import PDFViewer from './components/PDFViewer';
import ChatInterface from './components/ChatInterface';
import AgentSelector from './components/AgentSelector';
import AnalysisResults from './components/AnalysisResults';
import { uploadPDF, runAnalysis as apiRunAnalysis } from './services/api';

function App() {
  // App states
  const [appState, setAppState] = useState('landing'); // landing, uploading, main, direct-chat
  const [file, setFile] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showAgentSelector, setShowAgentSelector] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [rightPanelMode, setRightPanelMode] = useState('chat'); // 'chat' or 'analysis'

  // Handle file upload
  const handleFileUpload = async (uploadedFile) => {
    setFile(uploadedFile);
    setAppState('uploading');
    
    console.log("USE_BACKEND env var:", process.env.REACT_APP_USE_BACKEND);
    
    // Always try to use the backend first
    try {
      console.log("Attempting to upload to backend");
      await uploadToBackend(uploadedFile);
      console.log("Successfully uploaded to backend");
    } catch (error) {
      console.error('Error uploading file to backend:', error);
      alert("Could not connect to backend server. Using simulation mode instead.");
      // Fall back to simulation if upload fails
      simulateUpload();
    }
  };

  // Handle starting a direct chat without uploading a PDF
  const handleStartDirectChat = () => {
    // Generate a session ID for the direct chat
    const directChatSessionId = 'direct-' + Math.random().toString(36).substr(2, 9);
    setSessionId(directChatSessionId);
    setAppState('direct-chat');
  };

  // Upload file to backend
  const uploadToBackend = async (uploadedFile) => {
    try {
      let progress = 0;
      const progressInterval = setInterval(() => {
        progress = Math.min(progress + 5, 95); // Cap at 95% until actual completion
        setUploadProgress(progress);
      }, 200);
      
      // We're now importing uploadPDF at the top of the file
      
      // Upload file to backend
      const response = await uploadPDF(uploadedFile, (percent) => {
        setUploadProgress(Math.min(90, percent)); // Cap at 90% for upload, save 10% for processing
      });
      
      // Clear the interval
      clearInterval(progressInterval);
      
      // Set session ID from response
      setSessionId(response.session_id);
      setUploadProgress(100);
      
      // Proceed to main app after a short delay
      setTimeout(() => {
        setAppState('main');
      }, 500);
      
    } catch (error) {
      console.error('Error uploading file:', error);
      // Show error message to user
      alert('Error uploading file. Please try again.');
      setAppState('landing');
    }
  };

  // Simulate upload progress for demo purposes
  const simulateUpload = () => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += 5;
      setUploadProgress(progress);
      if (progress >= 100) {
        clearInterval(interval);
        setSessionId('session-' + Math.random().toString(36).substr(2, 9));
        setAppState('main');
      }
    }, 200);
  };

  // Toggle agent selector
  const toggleAgentSelector = () => {
    setShowAgentSelector(!showAgentSelector);
  };

  // Run analysis with selected agents
  const runAnalysis = async (selectedAgents) => {
    console.log('Running analysis with agents:', selectedAgents);
    setShowAgentSelector(false);
    
    // Set loading state
    setAnalysisResults('loading');
    
    // Switch to analysis panel
    setRightPanelMode('analysis');
    
    try {
      // Call the backend API
      const response = await apiRunAnalysis(sessionId, currentPage, selectedAgents);
      
      // Format the response for the UI
      const formattedResults = {
        agents: selectedAgents,
        results: {},
        extractedText: response.extracted_text || ''
      };
      
      // Process the results from each agent
      selectedAgents.forEach(agent => {
        switch(agent) {
          case 'Citation':
            formattedResults.results.Citation = response.results.citation_analysis || null;
            break;
          case 'SFEE':
            formattedResults.results.SFEE = 
              response.results.compliance_checks?.sfee_compliance || null;
            break;
          case 'EOF':
            formattedResults.results.EOF = 
              response.results.compliance_checks?.eof_compliance || null;
            break;
          case 'Galenica':
            console.log('Processing Galenica agent results:', response.results.compliance_checks?.galenica_compliance);
            formattedResults.results.Galenica = 
              response.results.compliance_checks?.galenica_compliance || null;
            break;
          case 'EFEX':
            formattedResults.results.EFEX = 
              response.results.compliance_checks?.efex_compliance || null;
            break;
          case 'Grammar':
            formattedResults.results.Grammar = 
              response.results.compliance_checks?.grammar || null;
            break;
          default:
            break;
        }
      });
      
      // Update state with formatted results
      setAnalysisResults(formattedResults);
      
    } catch (error) {
      console.error('Error running analysis:', error);
      
      // Show error in results
      setAnalysisResults({
        agents: selectedAgents,
        error: true,
        errorMessage: error.message || 'An error occurred while analyzing the document',
        results: selectedAgents.reduce((acc, agent) => {
          acc[agent] = `Error analyzing with ${agent} agent: ${error.message}`;
          return acc;
        }, {})
      });
    }
  };
  
  // Return to chat from analysis view
  const handleBackToChat = () => {
    setRightPanelMode('chat');
  };

  // Handle returning to landing page
  const handleBackToLanding = () => {
    setAppState('landing');
    setFile(null);
    setSessionId(null);
    setCurrentPage(1);
    setTotalPages(0);
    setRightPanelMode('chat');
    setAnalysisResults(null);
  };

  return (
    <div className="min-h-screen bg-dark-darker text-white">
      {appState === 'landing' && (
        <Landing 
          onFileUpload={handleFileUpload} 
          onStartDirectChat={handleStartDirectChat}
        />
      )}
      
      {appState === 'uploading' && (
        <UploadProgress progress={uploadProgress} />
      )}
      
      {appState === 'direct-chat' && (
        <div className="flex flex-col h-screen">
          <div className="p-3 bg-dark border-b border-dark-lighter flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold">Direct Chat Session</h2>
            </div>
            <button 
              onClick={handleBackToLanding}
              className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-lg transition duration-200"
            >
              Back to Home
            </button>
          </div>
          <div className="flex-grow overflow-hidden">
            <ChatInterface 
              sessionId={sessionId} 
              isDirect={true}
            />
          </div>
        </div>
      )}
      
      {appState === 'main' && (
        <div className="flex h-screen">
          {/* PDF Viewer (Left Side) */}
          <div className="w-1/2 p-3 border-r border-dark overflow-auto">
            <PDFViewer 
              file={file} 
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              setTotalPages={setTotalPages}
            />
          </div>
          
          {/* Right Panel (Chat or Analysis) */}
          <div className="w-1/2 flex flex-col p-3">
            {rightPanelMode === 'chat' ? (
              /* Chat Interface */
              <>
                <div className="p-3 bg-dark border-b border-dark-lighter flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-semibold">Chat Session</h2>
                    <p className="text-sm text-gray-400">Page {currentPage} of {totalPages}</p>
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={handleBackToLanding}
                      className="bg-dark-lighter hover:bg-dark-light text-white px-4 py-2 rounded-lg transition duration-200"
                    >
                      Back to Home
                    </button>
                    <button 
                      onClick={toggleAgentSelector}
                      className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-lg transition duration-200"
                    >
                      Detailed Analysis
                    </button>
                  </div>
                </div>
                
                <ChatInterface 
                  sessionId={sessionId} 
                  currentPage={currentPage}
                />
              </>
            ) : (
              /* Analysis Results Panel */
              <>
                <div className="p-3 bg-dark border-b border-dark-lighter flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-semibold">Analysis Results</h2>
                    <p className="text-sm text-gray-400">Page {currentPage} of {totalPages}</p>
                  </div>
                  <button 
                    onClick={handleBackToChat}
                    className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-lg transition duration-200"
                  >
                    Back to Chat
                  </button>
                </div>
                
                <div className="flex-grow overflow-auto">
                  {analysisResults === 'loading' ? (
                    /* Loading state */
                    <div className="h-full flex items-center justify-center">
                      <div className="text-center">
                        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-lg font-medium">Running Analysis</p>
                        <p className="text-sm text-gray-400">This may take a few moments...</p>
                      </div>
                    </div>
                  ) : (
                    /* Analysis results content */
                    <div className="p-4">
                      <AnalysisResults 
                        results={analysisResults}
                        onClose={handleBackToChat}
                        inPanel={true}
                      />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
      
      {/* Agent Selector Modal */}
      {showAgentSelector && (
        <AgentSelector 
          onClose={() => setShowAgentSelector(false)}
          onRunAnalysis={runAnalysis}
        />
      )}
    </div>
  );
}

export default App;